import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AttributeFilter, ExternalEvaluationFilter, FilterItem, FilterModel, ParticipantNameFilter } from "../models/filter-models";
import { LoadingController } from "@ionic/angular";
import { BackendService } from "@tg4/http-infrastructure/dist/src";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

@Component({
    selector: "app-attribute-filter",
    templateUrl: "./attribute-filter.component.html",
    styleUrls: ["./attribute-filter.component.scss"],
})
export class AttributeFilterComponent implements OnInit {
    @Input() public filter: FilterModel;
    @Output() filterChanged = new EventEmitter();

    private _searchSubject: Subject<string> = new Subject();
    public open = false;
    public term: string = "";

    constructor(
        private _backEndService: BackendService,
        private _loadingController: LoadingController,
    ) {
        this._setSearchSubscription();
    }

    ngOnInit() {
        if (this.filter.isRequired && this.filter.items.every((x) => !x.selected)) {
            this.toggleSelectItem(0);
        }
        if (this.filter.isTextarea) this.term = this.filter.items.length > 0 ? this.filter.items[0].value : "";
    }

    private async initializeItems(search: string = "") {
        if (this.filter instanceof AttributeFilter) {
            this.filter.items = this.filter.items.filter((x) => x.selected);
            const items = await this.searchAttributes(search);
            this.filter.items.push(...items.filter((x) => !this.filter.items.some((i) => x.value === i.value)));
        } else if (this.filter instanceof ExternalEvaluationFilter) {
            this.filter.items = this.filter.items.filter((x) => x.selected);
            const items = await this.searchExternalEvaluation(search);
            this.filter.items.push(...items.filter((x) => !this.filter.items.some((i) => x.value === i.value)));
        } else if (this.filter instanceof ParticipantNameFilter) {
            this.filter.items = this.filter.items.filter((x) => x.selected);
            const items = await this.searchParticipantName(search);
            this.filter.items.push(...items.filter((x) => !this.filter.items.some((i) => x.value === i.value)));
        }
    }

    private async searchAttributes(search: string = ""): Promise<FilterItem[]> {
        let result: FilterItem[] = [];
        try {
            const filter = this.filter as AttributeFilter;
            const parameters = [
                { name: "type", value: filter.type.type as any },
                { name: "search", value: search },
                { name: "onlyAccessible", value: filter.onlyAccessible },
            ];

            if (filter.calibrationGroupId) parameters.push({ name: "calibrationGroupId", value: filter.calibrationGroupId });

            const response = await this._backEndService.get("searchAttributes", [], parameters).toPromise();
            const responseData = response.data as any[];
            result = responseData.map((x) => new FilterItem("attributeIds", x.name, x.id, false, filter.type.type));
        } catch (ex) {}
        return result;
    }

    private async searchExternalEvaluation(search: string = ""): Promise<FilterItem[]> {
        let result: FilterItem[];

        const filter = this.filter as ExternalEvaluationFilter;
        const parameters = [
            { name: "type", value: filter.type.type as any },
            { name: "search", value: search },
        ];

        const response = await this._backEndService.get("searchExternalEvaluationAttributes", [], parameters).toPromise();
        const responseData = response.data as any[];
        result = responseData.map((x) => new FilterItem("externalEvaluationAttributesIds", x.name, x.id, false, filter.type.type));

        return result;
    }

    private async searchParticipantName(search: string = ""): Promise<FilterItem[]> {
        let result: FilterItem[] = [];
        try {
            const filter = this.filter as ParticipantNameFilter;
            const parameters = [{ name: "search", value: search }];
            if (filter.calibrationGroupId) parameters.push({ name: "calibrationGroupId", value: filter.calibrationGroupId.toString() });

            const response = await this._backEndService.get("getGroupParticipants", [], parameters).toPromise();
            const responseData = response.data as any[];
            result = responseData.map((x) => new FilterItem("participantName", x.name, x.id, false, 0));
        } catch (ex) {}
        return result;
    }

    public async updateSearch(event) {
        this._searchSubject.next(event.target.value);
    }

    public setValue(event) {
        if (this.filter.items.length === 0) {
            this.filter.items.push(new FilterItem("filters.searchString", "searchString", event.target.value, true, 0));
            return;
        }
        this.filter.items[0].value = event.target.value;
    }

    private _setSearchSubscription() {
        this._searchSubject.pipe(distinctUntilChanged(), debounceTime(1000)).subscribe((searchValue: string) => {
            this.initializeItems(searchValue);
        });
    }

    public async toggleExpand() {
        this.open = !this.open;
        if (this.open) {
            this.initializeItems();
        }
    }

    public toggleSelectItem(index: number) {
        this.filter.toggleSelectItem(index);
        this.filterChanged.emit();
    }

    public dateChanged(index: number, value: any) {
        this.filter.items[index].selected = !!value;
    }
}
